import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import VolunteerSignup from '../pages/VolunteerSignup';
import FrontDoor from '../pages/FrontDoor';
import { getSession, useSession } from '../lib/session';
import { useFlag } from '../lib/flags';

/**
 * Sign-up / log-in gate, fired at the moment of commitment rather than when a
 * card is opened — so people can browse a course or an opportunity freely and
 * are only asked to identify themselves when they enrol, join, apply or donate.
 *
 * It renders as an overlay on top of whatever page asked for it, which means
 * that page stays mounted and keeps its state (selected checkboxes, chosen
 * amount, scroll position) while the user signs in.
 *
 * With the login wall on, this shows the same FrontDoor as the front door
 * itself — there should be exactly one way to sign in. It still matters even
 * then, because ?claim= and ?continue= links bypass the wall by design, so a
 * member arriving from an email can still reach a point of commitment without
 * a session. The legacy password form is only used when the wall is off.
 *
 * Usage inside a MorphButton, which aborts on a falsy return:
 *
 *   onClick={async () => {
 *     if (!validate()) return false;
 *     return requireAuth();
 *   }}
 */
let openGate = null;

export function requireAuth() {
  if (getSession()) return Promise.resolve(true);
  if (!openGate) return Promise.resolve(false);
  return new Promise((resolve) => openGate(resolve));
}

export default function AuthGateHost() {
  const [resolver, setResolver] = useState(null);
  const session = useSession();
  const wallOn = useFlag('login_wall');

  useEffect(() => {
    openGate = (resolve) => setResolver(() => resolve);
    return () => {
      openGate = null;
    };
  }, []);

  useEffect(() => {
    if (!resolver) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [resolver]);

  // FrontDoor has no submit callback — it simply signs the member in. So the
  // gate watches for the session appearing and settles itself.
  useEffect(() => {
    if (!resolver || !session) return;
    resolver(true);
    setResolver(null);
  }, [resolver, session]);

  if (!resolver) return null;

  const settle = (didAuth) => {
    resolver(didAuth);
    setResolver(null);
  };

  return (
    <div
      className="fixed inset-0 z-[70] overflow-y-auto bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-label="Sign up or log in"
      data-testid="auth-gate"
    >
      <div className="mx-auto max-w-[480px] min-h-full bg-background relative">
        {wallOn ? (
          <>
            <button
              type="button"
              onClick={() => settle(false)}
              aria-label="Close"
              data-testid="auth-gate-close"
              className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-muted flex items-center justify-center"
            >
              <X className="h-4 w-4" />
            </button>
            <FrontDoor />
          </>
        ) : (
          <VolunteerSignup onBack={() => settle(false)} onSubmit={() => settle(true)} />
        )}
      </div>
    </div>
  );
}
